/**
 * Journal dates.
 *
 * The "journal day" (`entry_date`, YYYY-MM-DD) is ALWAYS resolved in the user's
 * local time zone: if you write at 23:50 in Madrid, the entry is from today
 * even if it is already tomorrow in UTC. That is why `toISOString()` (UTC) is
 * NOT used here to derive day keys.
 */

const DATE_KEY_LENGTH = 10;
const PAD = 2;
const MONTH_OFFSET = 1;
const MS_PER_DAY = 86_400_000;
/** Position of "MM-DD" within "YYYY-MM-DD". */
const MONTH_DAY_START = 5;

/** Local day key (YYYY-MM-DD) for a given date (defaults to now). */
export function localDateKey(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + MONTH_OFFSET).padStart(PAD, "0");
  const d = String(date.getDate()).padStart(PAD, "0");
  return `${y}-${m}-${d}`;
}

/** Validates a YYYY-MM-DD day key. */
export function isDateKey(value: string): boolean {
  if (value.length !== DATE_KEY_LENGTH) return false;
  const parsed = parseDateKey(value);
  return parsed !== null && localDateKey(parsed) === value;
}

/** Parses a day key into a local Date (local noon: immune to DST). */
export function parseDateKey(key: string): Date | null {
  const [y, m, d] = key.split("-").map(Number);
  if (!y || !m || !d) return null;
  const NOON = 12;
  const date = new Date(y, m - MONTH_OFFSET, d, NOON);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Adds days to a day key (negative values subtract). */
export function addDaysToKey(key: string, days: number): string {
  const date = parseDateKey(key);
  if (!date) return key;
  date.setDate(date.getDate() + days);
  return localDateKey(date);
}

/** Difference in days between two keys (b - a). */
export function diffInDays(a: string, b: string): number {
  const da = parseDateKey(a);
  const db = parseDateKey(b);
  if (!da || !db) return 0;
  return Math.round((db.getTime() - da.getTime()) / MS_PER_DAY);
}

/** "MM-DD" of a day key — for "a day like today". */
export function monthDayOf(key: string): string {
  return key.slice(MONTH_DAY_START);
}

/** "YYYY-MM" of a day key — for monthly groupings. */
export function monthOf(key: string): string {
  return key.slice(0, MONTH_DAY_START + PAD);
}
